import React, { useState } from 'react';
import { FileDown, Loader2, CheckCircle, X } from 'lucide-react';
import { generatePDFReport, PDFReportOptions, PDFReportData } from '../../utils/pdfGenerator';

interface PDFExportButtonProps {
  data: PDFReportData;
}

export function PDFExportButton({ data }: PDFExportButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [options, setOptions] = useState<PDFReportOptions>({
    includeSummary: true,
    includeAlerts: true,
    includeStaff: true,
    includeClients: true,
    includeFinancials: true,
  });

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 100));
      generatePDFReport(data, options);
    } finally {
      setIsGenerating(false);
      setIsOpen(false);
    }
  };

  const toggleOption = (key: keyof PDFReportOptions) => {
    setOptions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 
                 text-white text-sm font-medium rounded-lg transition-colors"
      >
        <FileDown className="w-4 h-4" />
        Export PDF
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-72 bg-slate-800 border border-slate-700 
                        rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-200">Export Options</h3>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="text-slate-400 hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Select sections to include in the report
              </p>
            </div>

            <div className="p-3 space-y-2">
              <OptionToggle
                label="Executive Summary"
                checked={options.includeSummary}
                onChange={() => toggleOption('includeSummary')}
              />
              <OptionToggle
                label="Compliance Alerts"
                checked={options.includeAlerts}
                onChange={() => toggleOption('includeAlerts')}
                count={data.alertSummary.total}
              />
              <OptionToggle
                label="Staff Performance"
                checked={options.includeStaff}
                onChange={() => toggleOption('includeStaff')}
                count={data.staffMetrics.length}
              />
              <OptionToggle
                label="Client Utilization"
                checked={options.includeClients}
                onChange={() => toggleOption('includeClients')}
                count={data.clientUtilization.length}
              />
              <OptionToggle
                label="Financial Summary"
                checked={options.includeFinancials}
                onChange={() => toggleOption('includeFinancials')}
              />
            </div>

            <div className="p-3 border-t border-slate-700">
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !Object.values(options).some(Boolean)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 
                         bg-green-500 hover:bg-green-600 disabled:bg-slate-600 
                         disabled:cursor-not-allowed text-white text-sm font-medium 
                         rounded-lg transition-colors"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4" />
                    Generate PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OptionToggle({
  label,
  checked,
  onChange,
  count,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
  count?: number;
}) {
  return (
    <button
      onClick={onChange}
      className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
        checked 
          ? 'bg-blue-500/20 text-blue-400' 
          : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
      }`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
          checked ? 'bg-blue-500 border-blue-500' : 'border-slate-500'
        }`}>
          {checked && <CheckCircle className="w-3 h-3 text-white" />}
        </div>
        <span className="text-sm">{label}</span>
      </div>
      {count !== undefined && (
        <span className="text-xs bg-slate-700 px-2 py-0.5 rounded">
          {count}
        </span>
      )}
    </button>
  );
}
